import { array } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import joinClasses from 'webapp/helpers/join-classes';
import didUpdate from 'webapp/modifiers/did-update';
import FormInputComponent from 'webapp/components/form/input';
import FormInputDurationComponent from 'webapp/components/form/input-duration';
import LoaderSvgComponent from 'webapp/components/loader-svg';
import ToolTipComponent from 'webapp/components/tool-tip';

import type GoalValueRow from 'webapp/models-custom/goal/value-row';

interface Args {
	model: GoalValueRow;
	isTargetPercentual: boolean;
	isPreviousValuesLoading: boolean;
	isDuration: boolean;
	isValuesPercentual: boolean;
	isDisabled?: boolean;
	onValueChange?: (model: GoalValueRow, value: number | null) => void;
}

export default class GoalAdvancedRowComponent extends Component<Args> {
	// for editing
	@tracked value = '';
	@tracked percent = '';
	@tracked updateDurationInputValue = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.onModelValueChange();
	}

	get model() {
		return this.args.model;
	}

	get isValueDisabled() {
		return this.args.isTargetPercentual || this.args.isDisabled;
	}

	get isPercentDisabled() {
		return !this.args.isTargetPercentual || this.args.isDisabled;
	}

	get showPercentSign() {
		return this.args.isValuesPercentual;
	}

	get showDurationInput() {
		return this.args.isDuration;
	}

	get previousValueStr() {
		return this.model.previousPoint?.valueStr;
	}

	get previousTooltip() {
		return `Value for the same period last year: ${this.model.previousPoint?.timeLabelLong}`;
	}

	@action
	onDurationValueChange(value: number | null) {
		this.model.updateValue(value);
		this.onModelValueChange();

		if (this.args.onValueChange && value !== null) {
			this.args.onValueChange(this.model, value);
		}
	}

	@action
	onValueChange() {
		let value: number | null = parseFloat(this.value);
		if (isNaN(value)) {
			value = null;
		}
		this.model.updateValue(value);
		this.onModelValueChange();

		if (this.args.onValueChange && value !== null) {
			this.args.onValueChange(this.model, value);
		}
	}

	@action
	onPercentFocusOut() {
		let percent: number | null = parseFloat(this.percent);
		if (isNaN(percent)) {
			percent = 0;
		}
		this.model.updatePercent(percent);
		this.onModelValueChange();
	}

	@action
	onModelValueChange() {
		this.updateDurationInputValue = true;

		this.value = this.model.shownValue;
		if (this.args.isTargetPercentual) {
			this.percent = this.model.shownPercentFromPreviousRowValue;
		} else {
			this.percent = this.model.shownPercentFromPreviousPointValue;
		}

		next(() => {
			this.updateDurationInputValue = false;
		});
	}
	<template>
		<div
			class={{joinClasses (array "row" (if @model.isActive "row--current"))}}
			{{didUpdate
				this.onModelValueChange
				@model.shownValue
				@model.shownPercentFromPreviousPointValue
				@model.shownPercentFromPreviousRowValue
				@isTargetPercentual
			}}
		>
			<span class="row__label">{{@model.title}}</span>
			<div class="row__value {{if this.showPercentSign 'row__value--percent'}}">
				{{#if this.showDurationInput}}
					<FormInputDurationComponent
						@name="value-{{@model.periodStartTs}}"
						@value={{this.value}}
						@onChange={{this.onDurationValueChange}}
						@isDisabled={{this.isValueDisabled}}
						@forceUpdateFromValue={{this.updateDurationInputValue}}
					/>
				{{else}}
					<FormInputComponent
						name="value-{{@model.periodStartTs}}"
						disabled={{this.isValueDisabled}}
						@value={{this.value}}
						@loseFocusOnEnter={{true}}
						@focusOut={{this.onValueChange}}
					/>
					{{#if this.showPercentSign}}
						<span class="percent">%</span>
					{{/if}}
				{{/if}}
			</div>
			<div class="row__percent">
				<FormInputComponent
					name="perc-{{@model.periodStartTs}}"
					disabled={{this.isPercentDisabled}}
					@value={{this.percent}}
					@loseFocusOnEnter={{true}}
					@focusOut={{this.onPercentFocusOut}}
				/>
			</div>
			<span class="row__compare">
				{{#if @isPreviousValuesLoading}}<LoaderSvgComponent />{{/if}}
				{{#if this.previousValueStr}}
					<span>
						({{this.previousValueStr}})
						<ToolTipComponent
							@text={{this.previousTooltip}}
							@position="top"
							@maxWidth={{300}}
						/>
					</span>
				{{/if}}
			</span>
		</div>
	</template>
}
